import { useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface DonationDialogProps {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  buttonText?: string;
}

export const DonationDialog = ({ 
  variant = "outline", 
  size = "default",
  className = "",
  buttonText = "💝 Support Us"
}: DonationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const presetAmounts = [5, 10, 25, 50, 100];

  const handlePresetAmount = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (value: string) => {
    // Remove any non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    setCustomAmount(cleaned);
    setSelectedAmount(null);
  };

  const handleDonate = async () => {
    const amount = selectedAmount || parseFloat(customAmount);
    
    if (!amount || isNaN(amount)) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid donation amount.",
        variant: "destructive",
      });
      return;
    }

    if (amount < 5 || amount > 2500) {
      toast({
        title: "Amount Out of Range",
        description: "Donation must be between $5.00 and $2,500.00",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('create-donation-checkout', {
        body: { amount: Math.round(amount * 100) }, // Convert to cents
        headers: session?.access_token ? {
          Authorization: `Bearer ${session.access_token}`
        } : undefined
      });

      if (error) throw error;

      if (data?.url) {
        // Open Stripe Checkout in new tab
        window.open(data.url, '_blank');
        setOpen(false);
        toast({
          title: "Redirecting to Payment",
          description: "Opening Stripe Checkout in a new tab...",
        });
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process donation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Heart className="h-4 w-4 mr-2" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            Support RealWordsOnly
          </DialogTitle>
          <DialogDescription>
            Help us grow and maintain a platform for real conversations with real people. 
            Your donation keeps us running and ad-free!
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Preset amounts */}
          <div>
            <label className="text-sm font-medium mb-2 block">Quick Select</label>
            <div className="grid grid-cols-3 gap-2">
              {presetAmounts.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant={selectedAmount === amount ? "default" : "outline"}
                  onClick={() => handlePresetAmount(amount)}
                  className="w-full"
                >
                  ${amount}
                </Button>
              ))}
            </div>
          </div>

          {/* Custom amount */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Custom Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                type="text"
                placeholder="Enter custom amount"
                value={customAmount}
                onChange={(e) => handleCustomAmountChange(e.target.value)}
                className="pl-7"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Minimum: $5.00 • Maximum: $2,500.00
            </p>
          </div>

          {/* Impact message */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <p className="font-medium mb-1">Your Impact</p>
            <p className="text-muted-foreground text-xs">
              Every donation helps us maintain our servers, develop new features, 
              and keep the platform free from ads and surveillance.
            </p>
          </div>

          {/* Donate button */}
          <Button 
            onClick={handleDonate} 
            disabled={loading || (!selectedAmount && !customAmount)}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Heart className="h-4 w-4 mr-2" />
                Donate {selectedAmount ? `$${selectedAmount}` : customAmount ? `$${customAmount}` : ''}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
